import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, RefreshCw, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { neonQuery } from '../services/api';

// Tier 2 — Audit log viewer. Renders the auth_events table with filters + pagination.
// Subject + actor admin_id are resolved to staff_name server-side via LEFT JOIN to adm_users.

const EVENT_TYPES = [
  '', 'password_authenticated', 'password_auth_failed', 'login_refused_inactive', 'password_legacy_migrated',
  'passkey_authenticated', 'passkey_auth_failed', 'passkey_registered', 'passkey_revoked',
  'mfa_setup_required', 'mfa_enrolled', 'mfa_verified', 'mfa_verify_failed',
  'mfa_backup_code_used', 'mfa_admin_reset', 'mfa_required_redirect',
  'device_trusted', 'session_logout', 'session_invalidated', 'setup_code_issued',
  'rate_limit_exceeded', 'security_alert_config_changed', 'password_reset_requested', 'password_reset_completed', 'password_reset_failed',
];

const EVENT_COLOR = {
  password_authenticated: '#15803d', passkey_authenticated: '#15803d', mfa_verified: '#15803d',
  password_auth_failed: '#c0392b', login_refused_inactive: '#c0392b', passkey_auth_failed: '#c0392b', mfa_verify_failed: '#c0392b',
  passkey_revoked: '#b45309', mfa_admin_reset: '#b45309', session_invalidated: '#b45309',
};

const PAGE_SIZE = 50;

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ event_type: '', admin_id: '', since: '', until: '' });
  const [expanded, setExpanded] = useState(null); // row id whose details are expanded

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset };
      for (const [k, v] of Object.entries(filters)) {
        if (v) params[k] = v;
      }
      const data = await neonQuery('auth_events_query', params);
      setRows(Array.isArray(data) ? data : (data.rows || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [offset, filters]);

  useEffect(() => { load(); }, [load]);

  const fmt = (ts) => ts ? new Date(ts).toLocaleString() : '';

  return (
    <div style={{ padding: 24, maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <ClipboardList size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Auth Audit Log</h1>
        <button onClick={load} disabled={loading} title="Refresh"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 14px',
            background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.5 : 1 }}>
          <RefreshCw size={14} /> {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 6, border: '1px solid var(--gray-200)', padding: 12, marginBottom: 12, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 4 }}>Event type</label>
          <select value={filters.event_type} onChange={e => { setOffset(0); setFilters(f => ({ ...f, event_type: e.target.value })); }}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' }}>
            {EVENT_TYPES.map(t => <option key={t} value={t}>{t || '(all event types)'}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 4 }}>Subject admin_id</label>
          <input type="number" placeholder="e.g. 22" value={filters.admin_id}
            onChange={e => { setOffset(0); setFilters(f => ({ ...f, admin_id: e.target.value })); }}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 4 }}>Since</label>
          <input type="datetime-local" value={filters.since}
            onChange={e => { setOffset(0); setFilters(f => ({ ...f, since: e.target.value })); }}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 4 }}>Until</label>
          <input type="datetime-local" value={filters.until}
            onChange={e => { setOffset(0); setFilters(f => ({ ...f, until: e.target.value })); }}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
      </div>

      {error && <div style={{ background: '#fee', color: '#c00', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      <div style={{ background: '#fff', borderRadius: 6, border: '1px solid var(--gray-200)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>When</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Event</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Subject</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Actor</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>IP</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--gray-700)' }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr style={{ borderBottom: '1px solid var(--gray-200)', cursor: 'pointer' }}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--gray-600)' }}>{fmt(r.created_at)}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      background: (EVENT_COLOR[r.event_type] || 'var(--navy)') + '22',
                      color: EVENT_COLOR[r.event_type] || 'var(--navy)' }}>
                      {r.event_type}
                    </span>
                  </td>
                  <td style={{ padding: '6px 10px' }}>{r.subject_name || (r.admin_id ? '#' + r.admin_id : '—')}</td>
                  <td style={{ padding: '6px 10px', color: 'var(--gray-600)' }}>{r.actor_name || (r.actor_admin_id ? '#' + r.actor_admin_id : '—')}</td>
                  <td style={{ padding: '6px 10px', color: 'var(--gray-600)', fontFamily: 'monospace', fontSize: 11 }}>{r.ip || '—'}</td>
                  <td style={{ padding: '6px 10px', color: 'var(--gray-500)', fontSize: 11 }}>
                    {r.details ? (expanded === r.id ? '▼ collapse' : '▶ ' + Object.keys(r.details).slice(0, 3).join(', ')) : ''}
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr style={{ background: 'var(--gray-50)' }}>
                    <td colSpan={6} style={{ padding: 10 }}>
                      <pre style={{ fontFamily: 'monospace', fontSize: 11, margin: 0, color: 'var(--gray-700)' }}>{JSON.stringify({ details: r.details, user_agent: r.user_agent, credential_id_prefix: r.credential_id_prefix }, null, 2)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}>No events found for the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', marginTop: 12 }}>
        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>
          rows {offset + 1}–{offset + rows.length}
        </span>
        <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#fff',
            color: 'var(--navy)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: offset === 0 ? 'default' : 'pointer', fontSize: 11, opacity: offset === 0 ? 0.4 : 1 }}>
          <ChevronLeft size={12} /> Prev
        </button>
        <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={rows.length < PAGE_SIZE}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#fff',
            color: 'var(--navy)', border: '1px solid var(--gray-300)', borderRadius: 4, cursor: rows.length < PAGE_SIZE ? 'default' : 'pointer', fontSize: 11, opacity: rows.length < PAGE_SIZE ? 0.4 : 1 }}>
          Next <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
